import { htmlTemplates, getRandomTemplate, getTemplateById, TemplateData } from './templates/htmlTemplates';
import { getRandomCompanyTheme, getCompanyThemeByName } from './templates/companyThemes';

export interface RenderOptions {
  templateId?: string;
  companyName?: string;
  useAI?: boolean;
  style?: string;
  theme?: string;
}

export interface RenderResult {
  html: string;
  templateId: string;
  companyName: string;
  success: boolean;
  error?: string;
}

export class TemplateRenderer {
  /**
   * Generate a cover using default HTML templates
   */
  static generateDefaultCover(
    title: string,
    subtitle: string,
    options: RenderOptions = {}
  ): RenderResult {
    try {
      // Select template
      const template = options.templateId 
        ? getTemplateById(options.templateId) || getRandomTemplate()
        : getRandomTemplate();

      // Select company theme
      const theme = options.companyName
        ? getCompanyThemeByName(options.companyName) || getRandomCompanyTheme()
        : getRandomCompanyTheme();

      // Generate template data
      const templateData: TemplateData = {
        title,
        subtitle,
        theme
      };

      // Generate HTML
      const html = template.generate(templateData);

      return {
        html,
        templateId: template.id,
        companyName: theme.name,
        success: true
      };
    } catch (error) {
      return {
        html: '',
        templateId: '',
        companyName: '',
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Generate a cover using AI
   */
  static async generateAICover(
    title: string,
    subtitle: string,
    options: RenderOptions = {}
  ): Promise<RenderResult> {
    try {
      const response = await fetch('/api/generate-cover', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          title,
          subtitle,
          style: options.style,
          theme: options.theme
        }),
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to generate AI cover');
      }

      return {
        html: data.html,
        templateId: 'ai-generated',
        companyName: 'AI Generated',
        success: true
      };
    } catch (error) {
      return {
        html: '',
        templateId: '',
        companyName: '',
        success: false,
        error: error instanceof Error ? error.message : 'AI generation failed'
      };
    }
  }

  /**
   * Generate a cover with fallback system
   */
  static async generateCover(
    title: string,
    subtitle: string,
    options: RenderOptions = {}
  ): Promise<RenderResult> {
    // If AI is requested, try AI first with fallback to default
    if (options.useAI) {
      const aiResult = await this.generateAICover(title, subtitle, options);
      
      if (aiResult.success) {
        return aiResult;
      }

      // Fallback to default template
      console.warn('AI generation failed, falling back to default template:', aiResult.error);
      const defaultResult = this.generateDefaultCover(title, subtitle, options);
      
      // Add fallback indicator to the result
      return {
        ...defaultResult,
        error: `AI generation failed (${aiResult.error}), using default template`
      };
    }

    // Use default template
    return this.generateDefaultCover(title, subtitle, options);
  }

  /**
   * Convert HTML to downloadable blob
   */
  static htmlToBlob(html: string): Blob {
    return new Blob([html], { type: 'text/html' });
  }

  /**
   * Get available template options
   */
  static getAvailableTemplates() {
    return htmlTemplates.map(template => ({
      id: template.id,
      name: template.name
    }));
  }

  /**
   * Render HTML in an iframe for preview
   */
  static renderInIframe(html: string, container: HTMLElement): HTMLIFrameElement {
    // Remove existing iframe if present
    const existingIframe = container.querySelector('iframe');
    if (existingIframe) {
      existingIframe.remove();
    }

    // Create new iframe
    const iframe = document.createElement('iframe');
    iframe.style.width = '100%';
    iframe.style.height = '100%';
    iframe.style.border = 'none';
    iframe.style.borderRadius = '8px';
    iframe.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';

    // Set iframe content
    iframe.onload = () => {
      if (iframe.contentDocument) {
        iframe.contentDocument.open();
        iframe.contentDocument.write(html);
        iframe.contentDocument.close();
      }
    };

    container.appendChild(iframe);
    return iframe;
  }
}
